import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FolderOpen, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ItemCard from "@/components/portfolio/ItemCard";
import { Reveal, ChapterBadge } from "@/components/Reveal";
import { api, coverUrl } from "@/lib/api";
import { PORTFOLIO_DISCLAIMER } from "@/data/content";
import { useLenis } from "@/lib/scroll";

const PAGE = 24;

export default function PortfolioHub() {
  useLenis();
  const [categories, setCategories] = useState([]);
  const [filterMeta, setFilterMeta] = useState({ years: [], tags: [], media_types: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [type, setType] = useState("");
  const [year, setYear] = useState(0);
  const [tag, setTag] = useState("");
  const [shown, setShown] = useState(PAGE);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const browsing = !q && !cat && !type && !year && !tag;

  useEffect(() => {
    document.title = "Selected Work | Sahil Paruthi";
    Promise.all([
      api.get("/portfolio/categories").then((r) => r.data).catch(() => []),
      api.get("/portfolio/filters").then((r) => r.data).catch(() => ({ years: [], tags: [], media_types: [] })),
    ]).then(([cats, meta]) => {
      setCategories(cats);
      setFilterMeta(meta);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/portfolio/items", {
          params: { q, category: cat, media_type: type, year, tag, limit: shown, skip: 0 },
        })
        .then((r) => {
          setItems(r.data.items);
          setTotal(r.data.total);
        })
        .catch(() => {
          setItems([]);
          setTotal(0);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat, type, year, tag, shown]);

  const chip = (activeAttr, label, onClick, testid) => (
    <button
      key={testid}
      data-testid={testid}
      onClick={onClick}
      aria-pressed={activeAttr}
      className={`rounded-full text-xs font-medium px-4 py-2 border transition-colors duration-200 ${
        activeAttr
          ? "bg-charcoal-900 text-parchment border-charcoal-900"
          : "bg-transparent text-charcoal-600 border-charcoal-900/15 hover:border-copper-600/50 hover:text-copper-600"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-parchment text-charcoal-900">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ChapterBadge number="" label="Portfolio" />
          <Reveal className="mt-8 max-w-3xl">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-charcoal-900 leading-[1.06]">
              Selected <em className="italic text-copper-600">Work.</em>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-charcoal-600 leading-relaxed">
              A collection of creative work spanning more than 14 years across digital, social,
              advertising, communication, presentations, video and design.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-8 max-w-3xl">
            <div
              data-testid="portfolio-disclaimer"
              className="rounded-2xl border border-copper-600/25 bg-copper-100/50 px-6 py-5 sm:px-8 sm:py-6"
            >
              <p className="font-mono text-sm font-bold tracking-[0.25em] uppercase text-copper-600 mb-2.5">
                Disclaimer
              </p>
              <p className="font-serif italic text-base sm:text-lg text-charcoal-800 leading-relaxed">
                {PORTFOLIO_DISCLAIMER}
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-10">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-500" />
              <input
                data-testid="portfolio-search-input"
                value={q}
                onChange={(e) => { setQ(e.target.value); setShown(PAGE); }}
                placeholder="Search work — try “carousel”, “presentation”, “advertising”..."
                aria-label="Search portfolio"
                className="w-full bg-surface-white border border-charcoal-900/15 rounded-full pl-11 pr-5 py-3.5 text-sm text-charcoal-900 placeholder:text-charcoal-500/70 focus:outline-none focus:border-copper-600 focus:ring-1 focus:ring-copper-600 transition-colors"
              />
            </div>
          </Reveal>

          <Reveal className="mt-6 flex flex-wrap gap-2">
            {chip(cat === "", "All Categories", () => { setCat(""); setShown(PAGE); }, "filter-category-all")}
            {categories.map((c) =>
              chip(cat === c.slug, c.name, () => { setCat(cat === c.slug ? "" : c.slug); setShown(PAGE); }, `filter-category-${c.slug}`)
            )}
          </Reveal>

          {(filterMeta.media_types.length > 0 || filterMeta.years.length > 0 || filterMeta.tags.length > 0) && (
            <Reveal className="mt-3 flex flex-wrap items-center gap-2">
              {filterMeta.media_types.map((t) =>
                chip(type === t, t.charAt(0).toUpperCase() + t.slice(1), () => { setType(type === t ? "" : t); setShown(PAGE); }, `filter-type-${t}`)
              )}
              {filterMeta.years.map((y) =>
                chip(year === y, String(y), () => { setYear(year === y ? 0 : y); setShown(PAGE); }, `filter-year-${y}`)
              )}
              {filterMeta.tags.map((t) =>
                chip(tag === t, `#${t}`, () => { setTag(tag === t ? "" : t); setShown(PAGE); }, `filter-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`)
              )}
            </Reveal>
          )}

          {browsing && categories.length > 0 && (
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hub-category-grid">
              {categories.map((c, i) => (
                <Reveal key={c.id} delay={Math.min(i * 0.04, 0.3)}>
                  <Link
                    to={`/portfolio/${c.slug}`}
                    data-testid={`hub-category-${c.slug}`}
                    className="group block bg-surface-white border border-charcoal-900/10 rounded-2xl overflow-hidden hover:shadow-xl hover:border-copper-600/30 transition-[box-shadow,border-color] duration-300"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden bg-surface-muted">
                      {c.cover_path ? (
                        <img src={coverUrl(c)} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center group-hover:scale-[1.03] transition-transform duration-500"
                          style={{ backgroundImage: "repeating-linear-gradient(135deg, rgba(18,19,22,0.035) 0 1px, transparent 1px 14px)" }}
                        >
                          <span className="font-serif italic text-6xl text-charcoal-900/15 select-none">{c.name.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-lg font-semibold text-charcoal-900">{c.name}</h3>
                        <span className="font-mono text-[10px] tracking-[0.15em] text-charcoal-500 shrink-0">
                          {c.item_count} {c.item_count === 1 ? "WORK" : "WORKS"}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-charcoal-600 leading-relaxed line-clamp-2">{c.description}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-500 group-hover:text-copper-600 transition-colors">
                        View Work <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}

          <div className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-charcoal-500">
                {browsing ? "All Work" : `${total} ${total === 1 ? "result" : "results"}`}
              </span>
              <span className="h-px flex-1 bg-charcoal-900/10" />
            </div>

            {loading ? (
              <p className="text-sm text-charcoal-500 font-mono" data-testid="portfolio-loading">Loading work...</p>
            ) : items.length === 0 ? (
              <div
                data-testid="portfolio-empty-state"
                className="rounded-2xl border border-dashed border-charcoal-900/20 bg-surface-white/60 p-12 text-center"
              >
                <FolderOpen className="w-8 h-8 mx-auto text-copper-600/60" />
                <p className="mt-4 font-serif text-2xl text-charcoal-900">Work will be added here</p>
                <p className="mt-2 text-sm text-charcoal-500 max-w-md mx-auto leading-relaxed">
                  {browsing
                    ? "The archive is being curated. New work appears here automatically as it is published."
                    : "No published work matches these filters yet."}
                </p>
              </div>
            ) : (
              <>
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="portfolio-grid">
                  {items.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      categorySlug={catMap[item.category_id]?.slug || "work"}
                      categoryName={catMap[item.category_id]?.name || ""}
                    />
                  ))}
                </div>
                {items.length < total && (
                  <div className="mt-10 text-center">
                    <button
                      data-testid="portfolio-load-more"
                      onClick={() => setShown((s) => s + PAGE)}
                      className="rounded-full border border-charcoal-900/25 text-charcoal-900 text-sm font-medium px-8 py-3.5 hover:border-copper-600 hover:text-copper-600 transition-colors"
                    >
                      Load more ({total - items.length} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
